import i18n from 'i18next';
import TabletFilled from '@ant-design/icons/lib/icons/TabletFilled';
import TabletOutlined from '@ant-design/icons/lib/icons/TabletOutlined';
import WarningFilled from '@ant-design/icons/lib/icons/WarningFilled';
import WarningOutlined from '@ant-design/icons/lib/icons/WarningOutlined';

import { OpRawMenuItemType } from 'config/createRawMenuItems';
import { AdminFilledSvg } from 'components/svgs/AdminFilledSvg/AdminFilledSvg';
import { AdminOutlinedSvg } from 'components/svgs/AdminOutlinedSvg/AdminOutlinedSvg';
import { DeviceFilledSvg } from 'components/svgs/DeviceFilledSvg/DeviceFilledSvg';
import { DevicesOutlinedSvg } from 'components/svgs/DevicesOutlinedSvg/DevicesOutlinedSvg';
import { HomeFilledSvg } from 'components/svgs/HomeFilledSvg/HomeFilledSvg';
import { HomeOutlinedSvg } from 'components/svgs/HomeOutlinedSvg/HomeOutlinedSvg';
import { LocationFilledSvg } from 'components/svgs/LocationFilledSvg/LocationFilledSvg';
import { LocationOutlinedSvg } from 'components/svgs/LocationOutlinedSvg/LocationOutlinedSvg';
import { ReportSvg } from 'components/svgs/ReportSvg';
import { ReportFilledSvg } from 'components/svgs/ReportSvg/ReportFilledSvg';
import { SettingFilledSvg } from 'components/svgs/SettingFilledSvg/SettingFilledSvg';
import { SettingOutlinedSvg } from 'components/svgs/SettingOutlinedSvg/SettingOutlinedSvg';
import { UserGroupFilledSvg } from 'components/svgs/UserGroupFilledSvg/UserGroupFilledSvg';
import { UserGroupOutlineSvg } from 'components/svgs/UserGroupOutlineSvg/UserGroupOutlineSvg';
import { VisitorScopeGroupCode } from 'constants/visitorScopes';

import { visitorRoutes } from './routes';

const baseVisitorMenuScopes = ['o:w', 'o:r', 's-o:r', 's-o:w'];

const getVisitorMenuScopes = (
  ...scopeGroups: VisitorScopeGroupCode[]
): string[] => [
  ...scopeGroups.flatMap((scopeGroup) => [
    `o{orgId}-${scopeGroup}:r`,
    `o{orgId}-${scopeGroup}:w`,
  ]),
  ...baseVisitorMenuScopes,
];

export const dashboardMenuItem = (): OpRawMenuItemType => ({
  key: visitorRoutes.dashboard,
  get title() {
    return i18n.t('sideMenu.home');
  },
  route: visitorRoutes.dashboard,
  icon: <HomeOutlinedSvg />,
  selectedIcon: <HomeFilledSvg />,
  scope: getVisitorMenuScopes('visitorDash'),
});

export const visitorsMenuItem = (): OpRawMenuItemType => ({
  key: visitorRoutes.visitors,
  get title() {
    return i18n.t('sideMenu.visitors');
  },
  route: visitorRoutes.visitors,
  icon: <UserGroupOutlineSvg />,
  selectedIcon: <UserGroupFilledSvg />,
  // Show the Visitors page when the user has the parent visitorVisitors scope
  // OR any of its child scopes (see helium seed-visitor-scope: visitorInvite,
  // visitorAllVisitors, visitorVisitorLog, visitorSignIn, visitorSignOut,
  // visitorDeny, visitorRedFlag, visitorGuestPass, visitorVisitorsBulk all have
  // visitorVisitors as their parent). A granular role granted only a child
  // scope still needs access to the Visitors page.
  scope: getVisitorMenuScopes(
    'visitorVisitors',
    'visitorInvite',
    'visitorAllVisitors',
    'visitorVisitorLog',
    'visitorSignIn',
    'visitorSignOut',
    'visitorDeny',
    'visitorRedFlag',
    'visitorGuestPass',
    'visitorVisitorsBulk',
  ),
});

export const locationsMenuItem = (): OpRawMenuItemType => ({
  key: visitorRoutes.locations,
  get title() {
    return i18n.t('sideMenu.locations');
  },
  route: visitorRoutes.locations,
  icon: <LocationOutlinedSvg />,
  selectedIcon: <LocationFilledSvg />,
  scope: getVisitorMenuScopes('visitorConfigurations', 'visitorLocation'),
});

export const kiosksMenuItem = (): OpRawMenuItemType => ({
  key: visitorRoutes.kiosks,
  get title() {
    return i18n.t('sideMenu.kiosks');
  },
  route: visitorRoutes.kiosks,
  icon: (
    <TabletOutlined
      style={{
        fontSize: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    />
  ),
  selectedIcon: (
    <TabletFilled
      style={{
        fontSize: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    />
  ),
  scope: getVisitorMenuScopes('visitorConfigurations', 'visitorKiosk'),
});

export const redFlagMenuItem = (): OpRawMenuItemType => ({
  key: visitorRoutes.redFlag,
  get title() {
    return i18n.t('sideMenu.redFlag');
  },
  route: visitorRoutes.redFlag,
  icon: (
    <WarningOutlined
      style={{
        fontSize: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    />
  ),
  selectedIcon: (
    <WarningFilled
      style={{
        fontSize: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    />
  ),
  scope: getVisitorMenuScopes('visitorConfigurations', 'visitorRedflagConf'),
});

export const reportsMenuItem = (integrations: {
  isGuestPassIntegrated: boolean;
  isTokenValid: boolean;
}): OpRawMenuItemType => {
  const { isGuestPassIntegrated, isTokenValid } = integrations;

  return {
    key: visitorRoutes.reports.root,
    get title() {
      return i18n.t('sideMenu.reports.title');
    },
    icon: <ReportSvg />,
    selectedIcon: <ReportFilledSvg />,
    scope: getVisitorMenuScopes(
      'visitorRpt',
      'visitorRptVisitor',
      'visitorRptTrend',
      'visitorRptRedFlag',
      'visitorRptGuestPass',
      'visitorRptHost',
      'visitorRptActivity',
    ),
    items: [
      {
        key: visitorRoutes.reports.visitorReport,
        get title() {
          return i18n.t('sideMenu.reports.visitorReport');
        },
        route: visitorRoutes.reports.visitorReport,
        scope: getVisitorMenuScopes('visitorRpt', 'visitorRptVisitor'),
      },
      {
        key: visitorRoutes.reports.visitorsHourlyReport,
        get title() {
          return i18n.t('sideMenu.reports.visitorsByHour');
        },
        route: visitorRoutes.reports.visitorsHourlyReport,
        scope: getVisitorMenuScopes('visitorRpt', 'visitorRptVisitor'),
      },
      {
        key: visitorRoutes.reports.repeatVisitors,
        get title() {
          return i18n.t('sideMenu.reports.repeatVisitors');
        },
        route: visitorRoutes.reports.repeatVisitors,
        scope: getVisitorMenuScopes('visitorRpt', 'visitorRptTrend'),
      },
      {
        key: visitorRoutes.reports.redFlagReport,
        get title() {
          return i18n.t('sideMenu.reports.redFlagReport');
        },
        route: visitorRoutes.reports.redFlagReport,
        scope: getVisitorMenuScopes('visitorRpt', 'visitorRptRedFlag'),
      },
      ...(isGuestPassIntegrated && isTokenValid
        ? [
            {
              key: visitorRoutes.reports.guestPassReport,
              get title() {
                return i18n.t('sideMenu.reports.guestPass');
              },
              route: visitorRoutes.reports.guestPassReport,
              scope: getVisitorMenuScopes('visitorRpt', 'visitorRptGuestPass'),
            },
            {
              key: visitorRoutes.reports.guestPassLog,
              get title() {
                return i18n.t('sideMenu.reports.guestPassLog');
              },
              route: visitorRoutes.reports.guestPassLog,
              scope: getVisitorMenuScopes('visitorRpt', 'visitorRptGuestPass'),
            },
          ]
        : []),
      {
        key: visitorRoutes.reports.hostReport,
        get title() {
          return i18n.t('sideMenu.reports.hostReport');
        },
        route: visitorRoutes.reports.hostReport,
        scope: getVisitorMenuScopes('visitorRpt', 'visitorRptHost'),
      },
      {
        key: visitorRoutes.reports.activityLog,
        get title() {
          return i18n.t('sideMenu.reports.activityLog');
        },
        route: visitorRoutes.reports.activityLog,
        scope: getVisitorMenuScopes('visitorRpt', 'visitorRptActivity'),
      },
    ],
  };
};

export const configMenuItem = (integrations: {
  isMsiIntegrated: boolean;
  isTokenValid: boolean;
}): OpRawMenuItemType => {
  const { isMsiIntegrated, isTokenValid } = integrations;

  return {
    key: visitorRoutes.configuration.root,
    get title() {
      return i18n.t('sideMenu.configuration.title');
    },
    icon: <SettingOutlinedSvg />,
    selectedIcon: <SettingFilledSvg />,
    scope: getVisitorMenuScopes(
      'visitorConfigurations',
      'visitorInvitation',
      'visitorSigninConf',
      'visitorSignoutConf',
      'visitorFields',
      'visitorAgreements',
      'visitorUser',
      'visitorEmailTemplates',
    ),
    items: [
      {
        key: visitorRoutes.configuration.invitations,
        get title() {
          return i18n.t('sideMenu.configuration.invitations');
        },
        route: visitorRoutes.configuration.invitations,
        scope: getVisitorMenuScopes(
          'visitorConfigurations',
          'visitorInvitation',
        ),
      },
      {
        key: visitorRoutes.configuration.workflow,
        get title() {
          return i18n.t('sideMenu.configuration.workflow');
        },
        route: visitorRoutes.configuration.workflow,
        scope: getVisitorMenuScopes(
          'visitorConfigurations',
          'visitorSigninConf',
          'visitorSignoutConf',
        ),
      },
      {
        key: visitorRoutes.configuration.formFields,
        get title() {
          return i18n.t('sideMenu.configuration.formFields');
        },
        route: visitorRoutes.configuration.formFields,
        scope: getVisitorMenuScopes('visitorConfigurations', 'visitorFields'),
      },
      {
        key: visitorRoutes.configuration.agreements,
        get title() {
          return i18n.t('sideMenu.configuration.agreements');
        },
        route: visitorRoutes.configuration.agreements,
        scope: getVisitorMenuScopes(
          'visitorConfigurations',
          'visitorAgreements',
        ),
      },
      {
        key: visitorRoutes.configuration.users,
        get title() {
          return i18n.t('sideMenu.configuration.users');
        },
        route: visitorRoutes.configuration.users,
        scope: getVisitorMenuScopes('visitorConfigurations', 'visitorUser'),
      },
      {
        key: visitorRoutes.configuration.userRoles,
        get title() {
          return i18n.t('sideMenu.configuration.roles');
        },
        route: visitorRoutes.configuration.userRoles,
        scope: [
          'o{orgId}-role:r',
          'o{orgId}-role:w',
          'o:w',
          'o:r',
          's-o:r',
          's-o:w',
          'o-role:r',
          'o-role:w',
        ],
      },
      {
        key: visitorRoutes.configuration.emailTemplates,
        get title() {
          return i18n.t('sideMenu.configuration.emailTemplates');
        },
        route: visitorRoutes.configuration.emailTemplates,
        scope: getVisitorMenuScopes(
          'visitorConfigurations',
          'visitorEmailTemplates',
        ),
      },
      ...(isMsiIntegrated && isTokenValid
        ? [
            {
              key: visitorRoutes.configuration.avigilonReaders,
              get title() {
                return i18n.t('sideMenu.configuration.avigilonReaders');
              },
              route: visitorRoutes.configuration.avigilonReaders,
              scope: getVisitorMenuScopes(
                'visitorConfigurations',
                'visitorSigninConf',
              ),
            },
          ]
        : []),
    ],
  };
};

export const adminMenuItem = (): OpRawMenuItemType => {
  return {
    key: visitorRoutes.administration.root,
    get title() {
      return i18n.t('sideMenu.administration.title');
    },
    icon: <AdminOutlinedSvg />,
    selectedIcon: <AdminFilledSvg />,
    scope: getVisitorMenuScopes('visitorAdmin', 'visitorIntegration'),
    items: [
      {
        key: visitorRoutes.administration.integration,
        get title() {
          return i18n.t('sideMenu.administration.integration');
        },
        route: visitorRoutes.administration.integration,
        scope: getVisitorMenuScopes('visitorAdmin', 'visitorIntegration'),
      },
    ],
  };
};

export const mobileMenuItem = (): OpRawMenuItemType => ({
  key: visitorRoutes.mobile,
  get title() {
    return i18n.t('sideMenu.mobile');
  },
  route: visitorRoutes.mobile,
  icon: <DevicesOutlinedSvg />,
  selectedIcon: <DeviceFilledSvg />,

  // scope: [
  //   'o{orgId}-parcelMgmtParcels:r',
  //   'o{orgId}-parcelMgmtParcels:w',
  //   'o:w',
  //   'o:r',
  //   's-o:r',
  //   's-o:w',
  // ],
});
export const sidebarItems = (integrations: {
  isMsiIntegrated: boolean;
  isGuestPassIntegrated: boolean;
  isTokenValid: boolean;
}): OpRawMenuItemType[] => [
  dashboardMenuItem(),
  visitorsMenuItem(),
  locationsMenuItem(),
  kiosksMenuItem(),
  redFlagMenuItem(),
  reportsMenuItem(integrations),
  configMenuItem(integrations),
  adminMenuItem(),
  mobileMenuItem(),
];
